import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userStatus, setUserStatus] = useState('unverified');

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = authService.getStoredUserData();
        const status = authService.getUserStatus();
        setUser(userData);
        setUserStatus(status);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Step 1: Signup - mobile + password, sends OTP
   */
  const signup = async (mobileNumber, password) => {
    try {
      const response = await authService.ManagerSignup(mobileNumber, password);
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Step 2: Verify OTP
   */
  const verifyOtp = async (mobileNumber, code) => {
    try {
      const response = await authService.verifyOtp(mobileNumber, code);
      if (response?.token) {
        localStorage.setItem('userStatus', 'unverified');
        setUserStatus('unverified');
        setIsAuthenticated(true);
        const userData = authService.getStoredUserData();
        setUser(userData);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Resend OTP
   */
  const resendOtp = async (mobileNumber) => {
    try {
      return await authService.resendOtp(mobileNumber);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Step 3: Submit signature and policy agreement
   */
  const submitAgreement = async (signatureData, agreedToTerms, agreedVersion) => {
    try {
      const userId = localStorage.getItem('userId');
      const response = await authService.submitAgreement(userId, signatureData, {
        agreedToTerms,
        agreedVersion: agreedVersion || '1.0',
        agreedAt: new Date().toISOString(),
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Step 4: Submit documents after OTP verification
   */
  const submitDocuments = async (documentData) => {
    try {
      const userId = localStorage.getItem('userId');
      const response = await authService.submitDocuments(userId, documentData);
      if (response?.data?.status === 'pending') {
        setUserStatus('pending');
        localStorage.setItem('userStatus', 'pending');
        // Update user in context
        if (user) {
          setUser({ ...user, status: 'pending' });
          localStorage.setItem('userData', JSON.stringify({ ...user, status: 'pending' }));
        }
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Upload image to S3
   */
  const uploadImage = async (fileData, fileName, fileType) => {
    try {
      return await authService.uploadImage(fileData, fileName, fileType);
    } catch (error) {
      throw error;
    }
  };

  const login = async (identifier, password) => {
    try {
      const response = await authService.login(identifier, password);
      if (response?.token) {
        setUser(response?.user || null);
        setIsAuthenticated(true);
        setUserStatus(response?.user?.status || 'unverified');
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setUserStatus('unverified');
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      setIsAuthenticated(false);
      setUserStatus('unverified');
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      if (userData?.status) {
        setUserStatus(userData.status);
        localStorage.setItem('userStatus', userData.status);
      }
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    userStatus,
    login,
    logout,
    refreshUser,
    signup,
    verifyOtp,
    resendOtp,
    submitAgreement,
    submitDocuments,
    uploadImage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;